import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useItemCategoryService from '../../app/services/hooks/useItemCategoryService';
import { ItemCategory, ItemCategoryFilters, defaultItemCategoryFilters } from '../../app/models/ItemCategory';
import ItemCategoryDataTable from './components/tables/ItemCategoryDataTable';
import ItemCategoryEdit from './components/ItemCategoryEdit';
import ItemCategoryCreate from './components/ItemCategoryCreate';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Categorías de Items',
        url: '/item-categories',
        isActive: true
    }
];

const ItemCategories = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetCategories, getCategories, deleteCategory } = useItemCategoryService();
    const [categories, setCategories] = useState<ItemCategory[]>([]);
    const [categoryIdEdit, setCategoryIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [categoryFilter, setCategoryFilter] = useState<ItemCategoryFilters>(defaultItemCategoryFilters);

    useEffect(() => {
        reloadTable();
    }, [categoryFilter]);

    const getAllCategories = () => {
        getCategories(categoryFilter, {
            onSuccess: (response: ServiceResponse) => {
                setCategories(response.data.item_categories);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (categoryId: number) => {
        setShowingEdit(true);
        setCategoryIdEdit(categoryId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setCategoryIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllCategories();
    };

    const destroy = (categoryId: number) => {
        const _text = 'Está a punto de eliminar la categoría #' + categoryId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando categoría...');
                        deleteCategory(categoryId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La categoría no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Categorías de Items" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva categoría" />}
                />
                <ItemCategoryDataTable
                    categories={categories}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetCategories}
                    filter={categoryFilter}
                    setFilter={setCategoryFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Categoría"
                    backdrop={true}
                    showFooter={false}
                >
                    <ItemCategoryCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Categoría"
                    backdrop={true}
                    showFooter={false}
                >
                    <ItemCategoryEdit
                        categoryId={categoryIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default ItemCategories;
